import { useEffect, useMemo } from 'react';
import { Activity } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../common/EmptyState';
import { useActivityLog } from '../../hooks/useActivityLog';
import { useAuthStore } from '../../stores/authStore';
import { formatActivity } from '../../utils/activityFormatter';
import type { ScheduleUser } from '../schedule/scheduleHelpers';

const ENTITY_BADGES: Record<string, { bg: string; fg: string }> = {
  invoice: { bg: palette.greenWash, fg: palette.green },
  expense: { bg: palette.amberWash, fg: palette.amber },
  schedule_entry: { bg: palette.blueWash, fg: palette.blue },
  work_order: { bg: palette.greenWash, fg: palette.green },
  booking: { bg: '#FFF7ED', fg: '#CA5010' },
  user: { bg: palette.accentWash, fg: palette.accent },
  shift: { bg: palette.blueWash, fg: palette.blue },
  leave_request: { bg: palette.amberWash, fg: palette.amber },
  team_invite_code: { bg: palette.accentWash, fg: palette.accent },
  invoice_settings: { bg: palette.g100, fg: palette.g500 },
  business: { bg: palette.g100, fg: palette.g500 },
  recurring_invoice: { bg: palette.greenWash, fg: palette.green },
};

interface Props {
  businessId: string | undefined;
  users: ScheduleUser[];
}

export function ActivityLogTab({ businessId, users }: Props) {
  const { entries, loading, hasMore, loadFirst, loadMore, filters, updateFilters } = useActivityLog(businessId);
  const user = useAuthStore(s => s.user);

  useEffect(() => { loadFirst(); }, [loadFirst]);

  const userMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.id, `${u.first_name} ${u.last_name.charAt(0)}.`);
    if (user) m.set(user.id, `${user.first_name} ${user.last_name.charAt(0)}. (you)`);
    return m;
  }, [users, user]);

  const entityTypes = useMemo(() => {
    const s = new Set<string>();
    for (const e of entries) s.add(e.entity_type);
    return Array.from(s).sort();
  }, [entries]);

  const SEL: React.CSSProperties = {
    padding: '5px 10px', borderRadius: 6, border: `1px solid ${palette.g200}`,
    fontSize: 12, fontFamily: 'inherit', color: palette.g600, background: palette.white,
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={SEL} value={filters.entityType ?? ''} onChange={e => updateFilters({ entityType: e.target.value || null })}>
          <option value="">All types</option>
          {entityTypes.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
        </select>
        <select style={SEL} value={filters.userId ?? ''} onChange={e => updateFilters({ userId: e.target.value || null })}>
          <option value="">All users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
        </select>
        <span style={{ fontSize: 12, color: palette.g400 }}>{entries.length} entries</span>
      </div>

      {/* Entries */}
      {entries.length === 0 && !loading ? (
        <EmptyState icon={<Activity size={22} />} title="No activity recorded yet" subtitle="Actions taken across CrewBit will appear here." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {entries.map(entry => {
            const badge = ENTITY_BADGES[entry.entity_type] ?? { bg: palette.g100, fg: palette.g500 };
            const who = userMap.get(entry.logged_by) ?? 'System';
            const when = new Date(entry.created_at);
            const timeStr = when.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
            const dateStr = when.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });

            return (
              <div key={entry.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0',
                borderBottom: `1px solid ${palette.g100}`,
                transition: 'background 150ms ease', borderRadius: 4,
              }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F3F4F6')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <div style={{ width: 40, textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: palette.g400, fontWeight: 500 }}>{timeStr}</div>
                  <div style={{ fontSize: 10, color: palette.g300 }}>{dateStr}</div>
                </div>
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                  background: badge.bg, color: badge.fg, whiteSpace: 'nowrap', flexShrink: 0,
                  textTransform: 'capitalize',
                }}>{entry.entity_type.replace('_', ' ')}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: palette.g800, lineHeight: 1.4 }}>{formatActivity(entry.activity_type, entry.description).text}</div>
                  <div style={{ fontSize: 11, color: palette.g400, marginTop: 2 }}>{who}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {loading && <div style={{ padding: 16, textAlign: 'center', color: palette.g400, fontSize: 12 }}>Loading...</div>}

      {hasMore && !loading && entries.length > 0 && (
        <button onClick={loadMore} style={{
          width: '100%', padding: '10px 0', marginTop: 8, borderRadius: 6,
          border: `1px solid ${palette.g200}`, background: palette.white,
          fontSize: 12.5, fontWeight: 500, color: palette.g600,
          cursor: 'pointer', fontFamily: 'inherit',
        }}>Load more</button>
      )}
    </div>
  );
}
